import { useEffect, Fragment } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Toaster } from "@/components/ui/sonner";
import useReveal from "@/hooks/useReveal";
import { setPageSEO } from "@/lib/seo";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import WhatsAppFloat from "@/components/landing/WhatsAppFloat";
import FinalCTA from "@/components/landing/FinalCTA";
import { DOLORES } from "@/data/dolores";

const FAQS = [
  {
    q: "¿Estos dolores son normales en cualquier empresa que crece?",
    a: "Sí. Son los síntomas más comunes cuando una empresa crece en ventas y personas más rápido que su estructura de liderazgo. No significan que algo esté mal contigo — significan que falta una metodología que sostenga ese crecimiento.",
  },
  {
    q: "¿Cuánto tiempo toma ver resultados si mi empresa tiene varios de estos dolores?",
    a: "La mayoría de los equipos nota cambios en el primer trimestre: reuniones más productivas, prioridades claras y menos decisiones escalando innecesariamente al dueño.",
  },
  {
    q: "¿Esto aplica si mi equipo de liderazgo es pequeño (menos de 10 personas)?",
    a: "Sí. EOS está diseñado para empresas de entre 10 y 250 colaboradores. Mientras exista un equipo de liderazgo (aunque sea de 3-4 personas) que necesite alinearse y ejecutar, la metodología aplica.",
  },
  {
    q: "¿Por dónde se empieza si tengo varios de estos dolores a la vez?",
    a: "Se empieza por una Sesión Introductoria de 90 minutos donde revisamos cuáles de estos dolores son más urgentes en tu empresa y qué herramientas de EOS los resuelven primero. No se implementa todo de golpe.",
  },
];

// Se alternan 4 frases de CTA a lo largo de las 8 tarjetas de dolor (2 veces cada una)
// para que la página no se sienta repetitiva.
const CTA_LABELS = [
  "Tengamos una llamada, sin compromiso",
  "Te cuento cómo funciona EOS",
  "Hablemos 5 minutos y vemos si hay fit, sin compromiso",
  "¿Quieres saber más sobre EOS?",
];

export default function DoloresDelEmpresario() {
  useReveal();

  useEffect(() => {
    setPageSEO({
      title: "Los Dolores del Empresario que Frenan tu Crecimiento | Carlos Garcín EOS",
      description:
        "Empresa que depende del dueño, reuniones sin ejecución, equipo que no rinde cuentas. Descubre por qué pasa y cómo EOS lo resuelve, dolor por dolor.",
      path: "/dolores-del-empresario",
      keywords:
        "dolores del empresario, empresa depende del dueño, fundador cuello de botella, no puedo delegar, reuniones improductivas empresa, equipo no rinde cuentas, ventas suben utilidad baja, falta de indicadores empresa",
    });
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Inicio", item: "https://garcin.cl/" },
      { "@type": "ListItem", position: 2, name: "Los Dolores del Empresario", item: "https://garcin.cl/dolores-del-empresario" },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const midpoint = 4;

  return (
    <>
      <Header />
      <main>
        <section className="bg-[#0A1128] pt-28 pb-16" data-testid="dolores-hero">
          <div className="container-eos">
            <span className="text-[11px] font-bold tracking-[0.28em] uppercase text-[#F26522]">
              Los dolores del empresario
            </span>
            <h1 className="mt-4 font-display font-extrabold text-white leading-[1.1] tracking-[-0.02em] text-3xl sm:text-4xl lg:text-5xl max-w-3xl">
              ¿Tu empresa crece, pero tú sientes que{" "}
              <span className="text-[#F26522]">pierdes el control?</span>
            </h1>
            <p className="mt-5 max-w-2xl text-slate-300 text-base sm:text-lg leading-relaxed">
              No eres el único. Estos son los 8 dolores más comunes de un empresario cuya empresa creció más
              rápido que su estructura de liderazgo — por qué ocurren, y cómo EOS los resuelve, uno por uno.
            </p>
          </div>
        </section>

        <section className="pt-14 sm:pt-16 lg:pt-20 pb-8 bg-white" data-testid="dolores-content">
          <div className="container-eos max-w-6xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-7">
              {DOLORES.map((d, i) => (
                <Fragment key={d.id}>
                  <article
                    id={d.id}
                    data-testid={`dolor-card-${d.id}`}
                    className="reveal card-soft p-6 sm:p-7 flex flex-col"
                  >
                    <div className="flex items-center justify-between">
                      <div className="w-11 h-11 rounded-xl bg-[#F26522]/10 flex items-center justify-center shrink-0">
                        <d.icon className="w-5 h-5 text-[#F26522]" strokeWidth={1.7} />
                      </div>
                      <span className="font-display font-bold text-slate-200 text-2xl tabular-nums">
                        0{i + 1}
                      </span>
                    </div>

                    <h2 className="mt-4 font-display font-extrabold text-xl sm:text-2xl text-navy-800 leading-tight">
                      {d.title}
                    </h2>
                    <p className="mt-2 text-slate-500 text-sm sm:text-base">{d.bajada}</p>

                    <div className="mt-4 space-y-3 text-slate-600 leading-relaxed text-sm sm:text-base flex-1">
                      <p>
                        <span className="font-semibold text-navy-800">¿Por qué pasa esto? </span>
                        {d.causaRaiz}
                      </p>
                      <p>
                        <span className="font-semibold text-navy-800">Cómo lo resuelve EOS: </span>
                        {d.solucionEOS}
                      </p>
                    </div>

                    <div className="mt-5">
                      <Link
                        to="/contacto"
                        data-testid={`dolor-cta-btn-${d.id}`}
                        className="w-full inline-flex items-center justify-center gap-2 bg-[#F26522] hover:bg-[#d9561e] text-white font-bold text-sm py-2.5 px-4 rounded-xl transition-colors duration-200"
                      >
                        {CTA_LABELS[i % CTA_LABELS.length]} <ArrowRight className="w-4 h-4" />
                      </Link>

                      <p className="mt-3.5 text-xs text-slate-400 leading-relaxed">
                        <span className="font-semibold">También te puede interesar: </span>
                        <Link
                          to={d.primaryLink.to}
                          data-testid={`dolor-primary-link-${d.id}`}
                          className="text-slate-500 underline decoration-slate-300 hover:text-[#F26522] hover:decoration-[#F26522] transition-colors"
                        >
                          {d.primaryLink.label}
                        </Link>
                        {d.secondaryLink && (
                          <>
                            {" · "}
                            <Link
                              to={d.secondaryLink.to}
                              data-testid={`dolor-secondary-link-${d.id}`}
                              className="text-slate-500 underline decoration-slate-300 hover:text-[#F26522] hover:decoration-[#F26522] transition-colors"
                            >
                              {d.secondaryLink.label}
                            </Link>
                          </>
                        )}
                      </p>
                    </div>
                  </article>

                  {i === midpoint - 1 && (
                    <div
                      data-testid="dolores-testimonial-callout"
                      className="reveal lg:col-span-2 py-8 sm:py-10 rounded-2xl border border-[#F26522]/20 bg-[#FEF3EB]/40 px-6 sm:px-8"
                    >
                      <Quote className="w-6 h-6 text-[#F26522]" fill="currentColor" />
                      <p className="mt-3 italic text-navy-800 text-lg sm:text-xl leading-relaxed">
                        "Recomiendo a Carlos Garcín como Implementador de EOS."
                      </p>
                      <p className="mt-3 text-sm text-slate-500">
                        — Álvaro Añón, Founder & CEO de{" "}
                        <span className="text-[#F26522] font-semibold">Soho.lat</span>
                      </p>
                      <Link
                        to="/testimonios-eos"
                        data-testid="dolores-testimonial-callout-link"
                        className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#F26522] hover:underline"
                      >
                        Ver testimonios completos <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  )}
                </Fragment>
              ))}
            </div>
          </div>
        </section>

        <section className="pb-20 sm:pb-24 lg:pb-28 bg-white" data-testid="dolores-faq">
          <div className="container-eos max-w-3xl">
            <div className="reveal">
              <span className="eyebrow">Preguntas frecuentes</span>
              <h2 className="mt-3 font-display font-extrabold text-2xl sm:text-3xl text-navy-800 leading-tight">
                Sobre estos dolores y cómo se resuelven
              </h2>
            </div>
            <div className="mt-8 reveal">
              <Accordion type="single" collapsible className="w-full">
                {FAQS.map((f, i) => (
                  <AccordionItem
                    key={i}
                    value={`item-${i}`}
                    data-testid={`dolores-faq-item-${i + 1}`}
                    className="border-b border-slate-200"
                  >
                    <AccordionTrigger
                      data-testid={`dolores-faq-trigger-${i + 1}`}
                      className="text-left font-display font-bold text-navy-800 text-base sm:text-lg py-5 hover:no-underline hover:text-[#F26522] [&[data-state=open]]:text-[#F26522]"
                    >
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent
                      data-testid={`dolores-faq-content-${i + 1}`}
                      className="text-slate-600 text-base leading-relaxed pb-5 pr-2"
                    >
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        <FinalCTA />
      </main>
      <Footer />
      <WhatsAppFloat />
      <Toaster position="top-right" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
    </>
  );
}
